import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, App, theme } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CategoryListItem, AffectedVendor } from '@/types/api/category';
import { categoryApi } from '@/services/api';
import { useCategoryMutations } from '@/hooks/useCategories';
import { controlHeightTokens } from '@/styles/tokens';
import ReplaceCategoryModal from './ReplaceCategoryModal';

interface DeactivateCategoryModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  category: CategoryListItem | null;
}

const DeactivateCategoryModal = ({
  visible,
  onClose,
  onSuccess,
  category,
}: DeactivateCategoryModalProps) => {
  const { token } = theme.useToken();
  const { t } = useTranslation(['categories', 'vendors', 'common']);
  const { message } = App.useApp();
  const { toggleStatus } = useCategoryMutations();

  const [confirming, setConfirming] = useState(false);
  const [affectedVendors, setAffectedVendors] = useState<AffectedVendor[]>([]);
  const [customersUnavailable, setCustomersUnavailable] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);

  // Use ref to track fetch promise so we can await it if user clicks during loading
  const fetchPromiseRef = useRef<Promise<AffectedVendor[]> | null>(null);

  // Fetch affected vendors when modal opens
  const fetchAffectedVendors = useCallback(async () => {
    if (!category) return [];

    try {
      const response = await categoryApi.getAffectedVendors(category.categoryId);
      const vendors = response.vendors || [];
      setAffectedVendors(vendors);
      setCustomersUnavailable(response.customersUnavailable);
      return vendors;
    } catch (error) {
      console.error('Failed to fetch affected vendors:', error);
      setAffectedVendors([]);
      return [];
    }
  }, [category]);

  useEffect(() => {
    if (visible && category) {
      // Store the promise so we can await it later if needed
      fetchPromiseRef.current = fetchAffectedVendors();
    } else {
      // Reset state when modal closes
      setAffectedVendors([]);
      setCustomersUnavailable(false);
      setShowReplaceModal(false);
      fetchPromiseRef.current = null;
    }
  }, [visible, category, fetchAffectedVendors]);

  // Handle initial deactivate confirmation
  const handleDeactivateConfirm = async () => {
    if (!category) return;

    setConfirming(true);

    // Wait for fetch to complete if still in progress
    let vendors = affectedVendors;
    if (fetchPromiseRef.current) {
      vendors = await fetchPromiseRef.current;
    }

    // If there are affected vendors, show the replace modal
    if (vendors.length > 0) {
      setConfirming(false);
      setShowReplaceModal(true);
      return;
    }

    // No affected vendors, proceed with deactivation directly
    await performDeactivation();
  };

  // Perform the actual deactivation
  const performDeactivation = async () => {
    if (!category) return;

    setConfirming(true);
    try {
      // Use mutation hook for cache invalidation
      await toggleStatus(category.categoryId, false);

      message.success(t('categories:category.messages.deactivateSuccess'));
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to deactivate category:', error);
      message.error(t('categories:category.messages.deactivateFailed'));
    } finally {
      setConfirming(false);
    }
  };

  // Handle replacement and deactivation
  const handleReplace = async (replacementCategoryId: number | null) => {
    if (!category) return;

    setConfirming(true);
    try {
      // If user chose to replace, batch update vendors first
      if (replacementCategoryId !== null) {
        const result = await categoryApi.batchReplace(category.categoryId, replacementCategoryId);
        // Deactivate the category
        await toggleStatus(category.categoryId, false);
        // Show single success message for replace + deactivate;
        // be explicit when the customers leg was skipped (D-CAT-17)
        message.success(
          t(
            result.customersSkipped
              ? 'categories:category.replace.replacePartialSuccess'
              : 'categories:category.replace.replaceSuccess',
            { count: result.updatedCount ?? 0 }
          )
        );
      } else {
        // User chose "Not now" - just deactivate without replacement
        await toggleStatus(category.categoryId, false);
        message.success(t('categories:category.messages.deactivateSuccess'));
      }
      setShowReplaceModal(false);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to replace and deactivate category:', error);
      message.error(t('categories:category.replace.replaceFailed'));
    } finally {
      setConfirming(false);
    }
  };

  // Close replace modal and close entire flow
  const handleReplaceModalClose = () => {
    setShowReplaceModal(false);
    onClose();
  };

  return (
    <>
      {/* Step 1: Initial Deactivate Confirmation */}
      <Modal
        open={visible && !showReplaceModal}
        onCancel={onClose}
        title={
          <div className="tw-flex tw-items-center tw-gap-2">
            <ExclamationCircleOutlined style={{ color: token.colorError }} />
            <span>{t('categories:category.deactivate.warning')}</span>
          </div>
        }
        onOk={handleDeactivateConfirm}
        okText={t('categories:category.actions.deactivate')}
        cancelText={t('common:actions.cancel')}
        okButtonProps={{
          danger: true,
          loading: confirming,
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
        cancelButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
        width={450}
      >
        <p style={{ marginBottom: 0, color: token.colorTextSecondary }}>
          {t('categories:category.deactivate.warningDescription')}
        </p>
        {customersUnavailable && (
          <p style={{ marginBottom: 0, marginTop: 8, color: token.colorWarning }}>
            {t('categories:category.deactivate.customersUnavailable')}
          </p>
        )}
      </Modal>

      {/* Step 2: Replace Category Modal (only shown if there are affected vendors) */}
      {category && (
        <ReplaceCategoryModal
          visible={showReplaceModal}
          onClose={handleReplaceModalClose}
          onReplace={handleReplace}
          categoryName={category.category}
          categoryId={category.categoryId}
          affectedVendors={affectedVendors}
          customersUnavailable={customersUnavailable}
        />
      )}
    </>
  );
};

export default DeactivateCategoryModal;
